import { Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { DatePipe } from '@angular/common';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Observable, Subject, catchError, concatMap, of, takeUntil } from 'rxjs';
import { SubscriptionService } from '../../core/subscription/subscription.service';
import { SubscriptionPlan, SubscriptionPlanName, SubscriptionPrice, SubscriptionType } from '../../core/subscription/subscription';
import { UserService } from '../../core/user/user.service';
import { AgentService } from '../../core/agent/agent.service';
import { ScriptLoaderService } from '../../core/helpers/script-loader.service';
import { InternalAnalyticsService } from '../../core/internal-analytics.service';
import { GuidedSetupService, GuidedSetupStepType } from '../../guided-setup/guided-setup.service';
import { WinbackGateService } from './winback-gate.service';

declare const Stripe: any;

/**
 * Win-back card gate (ENG-2367). A canceled Stripe business whose trial was
 * reset from Nova (ENG-2063) sees this before anything else in the dashboard.
 * It cannot be dismissed: the only ways out are a card, Switch account or Log
 * out. The card subscribes the business to Pro (the API grants the reset trial
 * days), reissues the phone number if it was released, and lands on Go Live.
 *
 * Rendered as our own overlay rather than an ngx-smart-modal: other shell code
 * calls modal.closeAll() on state refreshes, which would close a hard gate.
 */
@Component({
  selector: 'rosie-winback-modal',
  templateUrl: './winback-modal.component.html',
  styleUrls: ['./winback-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class WinbackModalComponent implements OnInit, OnDestroy {
  zipControl = new UntypedFormControl('', [Validators.required, Validators.pattern(/^\d{5}$/)]);
  promoControl = new UntypedFormControl('');

  promoState: 'collapsed' | 'expanded' | 'applied' = 'collapsed';
  promoInProgress = false;
  promoError = '';
  appliedPromoName = '';

  cardError = '';
  inProgress = false;
  submitAttempted = false;

  cardNumberReady = false;
  cardExpiryReady = false;
  cardCvcReady = false;

  monthlyPrice: SubscriptionPrice | null = null;

  private stripe: any;
  private stripeElements: any;
  private cardNumberElement: any;
  private cardExpiryElement: any;
  private cardCvcElement: any;
  private cardNumberComplete = false;
  private cardExpiryComplete = false;
  private cardCvcComplete = false;
  private readonly stripeScript = { name: 'stripe', src: 'https://js.stripe.com/v3/' };
  private readonly datePipe = new DatePipe('en-US');
  private previousBodyOverflow = '';
  private destroy = new Subject<void>();

  constructor(private subscription: SubscriptionService, private user: UserService, private agent: AgentService,
    private scriptLoader: ScriptLoaderService, private analytics: InternalAnalyticsService,
    private setup: GuidedSetupService, private messageService: MessageService, private zone: NgZone,
    private host: ElementRef<HTMLElement>, private gate: WinbackGateService) {
  }

  get trialDays(): number {
    return this.user.subscription?.trialDays || 7;
  }

  get trialEndDate(): Date {
    const date = new Date();
    date.setDate(date.getDate() + this.trialDays);
    return date;
  }

  get hasMultipleBusinesses(): boolean {
    return this.user.hasMultipleBusinesses;
  }

  ngOnInit() {
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    this.loadPlan();
    this.syncAppliedCoupon();
    this.initStripe();
  }

  ngOnDestroy() {
    document.body.style.overflow = this.previousBodyOverflow;
    this.destroy.next();
    this.destroy.complete();
    this.cardNumberElement?.unmount();
    this.cardExpiryElement?.unmount();
    this.cardCvcElement?.unmount();
  }

  // Hard gate: Esc does nothing, and Tab stays inside the modal.
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  focusCard() { this.cardNumberElement?.focus(); }
  focusExpiry() { this.cardExpiryElement?.focus(); }
  focusCvc() { this.cardCvcElement?.focus(); }

  expandPromo() {
    this.promoState = 'expanded';
  }

  applyPromo() {
    const code = (this.promoControl.value || '').trim();
    const subscriptionId = this.user.subscription?.id;

    if (this.promoInProgress || !code || !subscriptionId) {
      return;
    }

    this.promoInProgress = true;
    this.promoError = '';

    // No price id: the invoice preview it triggers is not meaningful for an
    // ended subscription. Stripe still checks the code against the price when
    // the new subscription is created.
    this.subscription.validateCoupon(subscriptionId, code)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (coupon) => {
          this.appliedPromoName = coupon?.name || coupon?.code || code;
          this.promoState = 'applied';
          this.promoInProgress = false;
        },
        error: (error) => {
          this.subscription.appliedCoupon = null;
          this.promoInProgress = false;
          this.promoError = (typeof error === 'string' ? error : null) || 'Invalid Code';
        }
      });
  }

  removePromo() {
    this.subscription.appliedCoupon = null;
    this.appliedPromoName = '';
    this.promoError = '';
    this.promoControl.reset('');
    this.promoState = 'collapsed';
  }

  submit() {
    this.submitAttempted = true;
    this.zipControl.markAsTouched();

    if (this.inProgress) {
      return;
    }

    if (!this.stripe || !this.cardNumberElement || !this.monthlyPrice) {
      this.cardError = 'The payment form is still loading. Please try again in a moment.';
      return;
    }

    if (!this.cardComplete || this.zipControl.invalid) {
      this.cardError = this.cardComplete ? 'Enter a 5-digit ZIP code.' : 'Enter your full card details to start your trial.';
      return;
    }

    this.inProgress = true;
    this.cardError = '';

    this.stripe.confirmCardSetup(this.subscription.credentials.clientSecret, {
      payment_method: {
        card: this.cardNumberElement,
        billing_details: { address: { postal_code: this.zipControl.value } }
      }
    }).then((result: any) => this.zone.run(() => {
      if (result.error) {
        this.cardError = result.error?.code === 'card_declined'
          ? 'That card didn\'t go through. Prepaid cards are not supported. Want to try another?'
          : (result.error?.message || 'Something went wrong. Please try again.');
        this.inProgress = false;
        return;
      }

      this.startTrial(result.setupIntent.payment_method);
    })).catch(() => this.zone.run(() => {
      this.cardError = 'Something went wrong. Please try again.';
      this.inProgress = false;
      this.refreshSetupIntent();
    }));
  }

  logout() {
    this.user.logout();
  }

  // Deliberately no takeUntil(destroy): the subscription clears
  // trial_reset_pending, and a refresh inside handleSubscription drops it in
  // the dashboard. The gate latch keeps this component mounted, and these
  // steps must finish even if it is not.
  private startTrial(paymentMethod: string) {
    this.gate.completing = true;

    this.subscription.handleSubscription([this.monthlyPrice], false, paymentMethod, false)
      .pipe(
        concatMap(() => this.reissuePhoneNumberIfNeeded()),
        // The trial already exists here: a failed refresh must not show a card
        // error. Go Live loads the user again.
        concatMap(() => this.user.getUser().pipe(catchError(() => of(null))))
      )
      .subscribe({
        next: () => {
          this.analytics.fireCreditCardAddedEvent();
          this.showSuccessToast();
          // The button stays busy until Go Live is on screen, then the gate
          // closes.
          this.setup.goToStep(GuidedSetupStepType.Launch)
            .finally(() => this.gate.completing = false);
        },
        error: (error) => {
          this.gate.completing = false;
          this.cardError = `${error}`.toLowerCase().includes('coupon')
            ? 'Promotion code invalid. Please re-enter or remove code.'
            : 'That card didn\'t go through. Want to try another?';
          this.inProgress = false;
          this.refreshSetupIntent();
        }
      });
  }

  // The number is released some days after a cancellation. The assign
  // endpoint buys it synchronously, so Go Live shows it right away. A failure
  // here must not block the trial: Go Live still offers to reissue it.
  private reissuePhoneNumberIfNeeded(): Observable<unknown> {
    if (this.agent.phoneNumber) {
      return of(null);
    }

    return this.agent.reissuePhoneNumber().pipe(
      concatMap(() => this.agent.getAgent()),
      catchError(() => of(null))
    );
  }

  private showSuccessToast() {
    const trialEndsAt = this.user.subscription?.trialEndsAt ? new Date(this.user.subscription.trialEndsAt) : this.trialEndDate;

    this.messageService.add({
      severity: 'success',
      detail: `You're back in. Your trial runs through ${this.datePipe.transform(trialEndsAt, 'MMM d')}.`,
      life: 5000
    });
  }

  private loadPlan() {
    this.subscription.getPlans()
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (plans: SubscriptionPlan[]) => {
          const professional = (plans || []).find(plan => plan.name === SubscriptionPlanName.Professional);
          const licensedPrices = (professional?.prices || []).filter(price => price.usageType === SubscriptionType.Licensed);

          this.monthlyPrice = licensedPrices.find(price => price.interval === 'month') || licensedPrices[0] || null;
        },
        error: () => this.cardError = 'Unable to load your plan. Please refresh the page and try again.'
      });
  }

  // getPlans() can pre-apply a campaign coupon; show it so the user knows.
  private syncAppliedCoupon() {
    const coupon = this.subscription.appliedCoupon;

    if (coupon?.id) {
      this.appliedPromoName = coupon.name || coupon.code || coupon.id;
      this.promoState = 'applied';
    }
  }

  private initStripe() {
    this.scriptLoader.load(this.stripeScript).pipe(takeUntil(this.destroy)).subscribe({ next: () => { }, error: () => { } });

    this.subscription.getBilling()
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: () => this.whenStripeReady().pipe(takeUntil(this.destroy)).subscribe({
          next: () => this.mountCardFields(),
          error: () => this.cardError = 'Unable to load the payment form. Please refresh the page and try again.'
        }),
        error: () => this.cardError = 'Unable to load the payment form. Please refresh the page and try again.'
      });
  }

  private refreshSetupIntent() {
    this.subscription.getBilling().subscribe({ error: () => { } });
  }

  private whenStripeReady(): Observable<void> {
    return new Observable<void>((observer) => {
      const start = Date.now();
      const poll = () => {
        if (typeof Stripe !== 'undefined') {
          observer.next();
          observer.complete();
          return;
        }

        if (Date.now() - start > 10000) {
          observer.error('Stripe.js failed to load');
          return;
        }

        setTimeout(poll, 50);
      };

      poll();
    });
  }

  private mountCardFields() {
    const credentials = this.subscription.credentials;

    if (!credentials?.publishableKey || !credentials?.clientSecret) {
      this.cardError = 'Unable to load the payment form. Please refresh the page and try again.';
      return;
    }

    this.stripe = Stripe(credentials.publishableKey);
    this.stripeElements = this.stripe.elements({
      clientSecret: credentials.clientSecret,
      fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500&display=swap' }]
    });

    const isMobile = window.matchMedia('(max-width: 640px)').matches;
    const style = {
      base: {
        fontFamily: 'Outfit, sans-serif',
        fontSize: isMobile ? '16px' : '17px',
        fontWeight: '400',
        color: '#2a143c',
        '::placeholder': { color: '#7f728a' }
      },
      invalid: { color: '#be123c' }
    };

    this.cardNumberElement = this.stripeElements.create('cardNumber', { style, placeholder: 'Card number' });
    this.cardExpiryElement = this.stripeElements.create('cardExpiry', { style, placeholder: 'MM/YY' });
    this.cardCvcElement = this.stripeElements.create('cardCvc', { style, placeholder: 'CVC' });

    this.cardNumberElement.mount('#winback-card-number');
    this.cardExpiryElement.mount('#winback-card-expiry');
    this.cardCvcElement.mount('#winback-card-cvc');

    this.cardNumberElement.on('change', (event: any) => this.zone.run(() => {
      this.cardNumberComplete = event.complete;
      this.cardError = event.error?.message || '';
    }));
    this.cardExpiryElement.on('change', (event: any) => this.zone.run(() => {
      this.cardExpiryComplete = event.complete;
      this.cardError = event.error?.message || '';
    }));
    this.cardCvcElement.on('change', (event: any) => this.zone.run(() => {
      this.cardCvcComplete = event.complete;
      this.cardError = event.error?.message || '';
    }));

    this.cardNumberElement.on('ready', () => this.zone.run(() => {
      this.cardNumberReady = true;
      this.cardNumberElement.focus();
    }));
    this.cardExpiryElement.on('ready', () => this.zone.run(() => this.cardExpiryReady = true));
    this.cardCvcElement.on('ready', () => this.zone.run(() => this.cardCvcReady = true));
  }

  private trapFocus(event: KeyboardEvent) {
    const focusable = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])'
    ));

    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement;
    const insideModal = this.host.nativeElement.contains(active);

    if (!insideModal) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private get cardComplete(): boolean {
    return this.cardNumberComplete && this.cardExpiryComplete && this.cardCvcComplete;
  }
}
